import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";

export default function PortalInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);

  const load = () =>
    api.get("/invoices").then((r) => {
      setInvoices(r.data);
      setLoading(false);
    });

  useEffect(() => {
    load();
  }, []);

  const pay = async (id) => {
    setPaying(id);
    try {
      await api.post(`/invoices/${id}/pay`);
      toast.success("Payment recorded (mock).");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPaying(null);
    }
  };

  const outstanding = invoices.filter((i) => i.status !== "paid").reduce((s, i) => s + Number(i.amount), 0);
  const paid = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + Number(i.amount), 0);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Invoices</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tighter font-medium mt-2">Invoices &amp; payments</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
        <Stat label="Outstanding" value={`$${outstanding.toLocaleString()}`} testId="inv-outstanding" />
        <Stat label="Paid to date" value={`$${paid.toLocaleString()}`} testId="inv-paid" />
        <Stat label="Total invoices" value={invoices.length} testId="inv-count" />
      </div>

      <div className="bg-white border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-500">
            <tr>
              <th className="text-left px-5 py-3 font-semibold">Invoice</th>
              <th className="text-left px-5 py-3 font-semibold">Description</th>
              <th className="text-right px-5 py-3 font-semibold">Amount</th>
              <th className="text-left px-5 py-3 font-semibold">Status</th>
              <th className="text-right px-5 py-3 font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="5" className="px-5 py-10 text-center text-slate-400">Loading…</td></tr>}
            {!loading && invoices.length === 0 && (
              <tr><td colSpan="5" className="px-5 py-10 text-center text-slate-400">No invoices yet.</td></tr>
            )}
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-5 py-3 font-mono-alt text-xs text-slate-700">{inv.invoice_number}</td>
                <td className="px-5 py-3 text-slate-700">{inv.description}</td>
                <td className="px-5 py-3 text-right tabular-nums font-medium">${Number(inv.amount).toLocaleString()}</td>
                <td className="px-5 py-3"><StatusBadge status={inv.status} /></td>
                <td className="px-5 py-3 text-right">
                  {inv.status !== "paid" ? (
                    <button
                      data-testid={`pay-invoice-${inv.id}`}
                      onClick={() => pay(inv.id)}
                      disabled={paying === inv.id}
                      className="px-3 py-1.5 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-xs font-semibold rounded-sm disabled:opacity-60"
                    >
                      {paying === inv.id ? "Processing…" : "Pay now"}
                    </button>
                  ) : (
                    <span className="text-xs text-slate-400">
                      Paid {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : ""}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-slate-400">
        Payment processing is currently in preview — invoices are marked paid instantly. Stripe / Square integration will replace this flow.
      </div>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="bg-white p-5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="font-display text-3xl tracking-tighter tabular-nums mt-2 text-[#0B192C]">{value}</div>
    </div>
  );
}
